import { useCallback, useEffect, useRef, useState } from 'react';
import { V3_BASE_URL } from '@appmaker/core/api/core';
import { useProject } from '@appmaker/core/hooks';
import { toast } from '@appmaker/components/ReactHotToastIntegrator';
import axios from 'axios';

const requestConfig = { credentials: 'include', withCredentials: true };

const checkedAccess = (data) => {
    if (!Array.isArray(data?.missingScopes) || data.reauthRequired !== (data.missingScopes.length > 0)) {
        throw new Error('Unable to verify Shopify permissions. Please retry.');
    }
    return data;
};

const getErrorMessage = (error) => {
    const responseData = error?.response?.data;

    if (typeof responseData === 'string') return responseData;

    return (
        responseData?.message ||
        responseData?.error?.message ||
        (typeof responseData?.error === 'string' ? responseData.error : null) ||
        error?.message ||
        'Unable to configure webhook'
    );
};

const BackInStockHeader = () => {
    const { projectId } = useProject();
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(true);
    const [checkError, setCheckError] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [scopeAccess, setScopeAccess] = useState(null);
    const checks = useRef({ version: 0, awaitingPermissionReturn: false });
    const [deletingId, setDeletingId] = useState(null);
    const [pendingCleanup, setPendingCleanup] = useState(null);
    const [pendingSync, setPendingSync] = useState(null);

    async function deleteWebhook(id, retry = false) {
        if (!retry && !window.confirm('Delete this webhook? Shopify will stop sending inventory updates to this connection. Existing back-in-stock subscriptions will be kept.')) return;
        setDeletingId(id);
        try {
            const { data } = await axios.post(`${baseUrl}/delete-webhook`, { webhookSubscriptionId: id }, requestConfig);
            if (!data?.status) {
                const error = new Error(data?.message || 'Unable to delete webhook');
                error.response = { data };
                throw error;
            }
            setPendingCleanup(null);
            setPendingSync(current => current === id ? null : current);
            setSubscriptions(current => current.filter(sub => sub.id !== id));
            toast.success('Webhook deleted');
            await fetchSubscriptions();
        } catch (error) {
            if (error?.response?.data?.deleted) {
                setPendingCleanup(id);
                setSubscriptions(current => current.filter(sub => sub.id !== id));
            }
            toast.error(getErrorMessage(error));
        } finally {
            setDeletingId(null);
        }
    }

    const projectBaseUrl = `${V3_BASE_URL}/projects/${projectId}`;
    const baseUrl = `${projectBaseUrl}/extension-functions/back-in-stock`;

    const fetchSubscriptions = useCallback(async () => {
        const checkState = checks.current;
        // A manual check also satisfies the pending return from Shopify.
        checkState.awaitingPermissionReturn = false;
        const version = ++checkState.version;
        setChecking(true);
        setCheckError(null);
        setScopeAccess(null);
        try {
            const { data } = await axios.get(`${baseUrl}/access-scopes`, {
                ...requestConfig, params: { scopes: 'read_inventory' },
            });
            if (version !== checkState.version) return;
            if (data?.status !== true) throw new Error(data?.message || 'Unable to verify Shopify permissions');
            const access = checkedAccess(data);
            setScopeAccess(access);
            if (access.reauthRequired) {
                setSubscriptions([]);
                return;
            }
            const response = await axios.get(`${baseUrl}/list-webhooks`, requestConfig);
            if (version !== checkState.version) return;
            const { status, subscriptions: subs = [] } = response.data || {};
            setSubscriptions(status ? subs : []);
            if (!status) {
                const message = response.data?.message || 'Unable to check webhook status';
                setCheckError(message);
                toast.error(message);
            }
        } catch (error) {
            if (version !== checkState.version) return;
            if (error?.response?.data?.reauthRequired) {
                try {
                    setScopeAccess(checkedAccess(error.response.data));
                } catch (accessError) {
                    setScopeAccess(null);
                    setCheckError(getErrorMessage(accessError));
                }
            } else {
                setCheckError(getErrorMessage(error));
            }
            setSubscriptions([]);
        } finally {
            if (version === checkState.version) setChecking(false);
        }
    }, [baseUrl]);

    useEffect(() => {
        if (!projectId) return;
        const checkState = checks.current;
        fetchSubscriptions();
        const refreshWhenVisible = (event) => {
            if (event.type === 'focus' && event.target !== window) return;
            // Both focus and visibilitychange can fire for the same tab return.
            // fetchSubscriptions consumes this flag before starting the request.
            if (checkState.awaitingPermissionReturn && document.visibilityState === 'visible') {
                fetchSubscriptions();
            }
        };
        window.addEventListener('focus', refreshWhenVisible);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        return () => {
            ++checkState.version;
            checkState.awaitingPermissionReturn = false;
            window.removeEventListener('focus', refreshWhenVisible);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
        };
    }, [projectId, fetchSubscriptions]);

    async function configureWebhook(webhookSubscriptionId) {
        setLoading(true);
        try {
            const response = await axios.post(
                `${baseUrl}/register-webhook`,
                webhookSubscriptionId ? { webhookSubscriptionId } : {},
                requestConfig
            );
            const { status, dryRun, reauthRequired, message, syncPending, webhookSubscription, registrySynced } =
                response.data || {};
            if (status && dryRun) {
                toast.success('Dry run — request logged to server console (not sent)');
                console.log('register-webhook dry-run request', response.data?.request);
            } else if (status && registrySynced === true) {
                setPendingSync(null);
                toast.success('Webhook configured');
                await fetchSubscriptions();
            } else if (reauthRequired) {
                await fetchSubscriptions();
                toast.error(
                    message ||
                        'Accept Shopify permissions to grant inventory access, then retry.'
                );
            } else {
                if ((syncPending || status) && webhookSubscription?.id) setPendingSync(webhookSubscription.id);
                toast.error(message || 'Back-in-stock sync was not confirmed. Please retry.');
            }
        } catch (error) {
            const data = error?.response?.data;
            if (data?.reauthRequired) await fetchSubscriptions();
            if (data?.syncPending && data?.webhookSubscription?.id) setPendingSync(data.webhookSubscription.id);
            toast.error(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    }

    const configured = subscriptions.length > 0;
    const busy = checking || loading || Boolean(deletingId);
    const permissionRequired = scopeAccess?.reauthRequired === true;
    const canConfigure = scopeAccess?.reauthRequired === false && !checkError;
    const buttonClass = 'inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
    const markPermissionReturn = (event) => {
        if (event.button === 0 || event.button === 1) checks.current.awaitingPermissionReturn = true;
    };
    const permissionsLink = scopeAccess?.missingScopes.includes('read_inventory') && (
        <a href={`${projectBaseUrl}/re-authorize-scopes?scopes=read_inventory`} target="_blank" rel="noopener noreferrer"
            onClick={markPermissionReturn} onAuxClick={markPermissionReturn}
            className={`${buttonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
            Accept Shopify permissions <span className="sr-only">(opens in a new tab)</span>
        </a>
    );

    return (
        <section aria-label="Back-in-stock configuration" aria-busy={checking || loading || Boolean(deletingId)}
            className="w-full min-w-0 rounded-lg border border-gray-200 bg-white text-left shadow-sm">
            <div className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">Inventory connection</h3>
                    <span role="status" className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${checking ? 'bg-gray-100 text-gray-600' : checkError || permissionRequired ? 'bg-yellow-100 text-yellow-800' : configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {checking ? 'Checking…' : checkError ? 'Unable to verify' : permissionRequired ? 'Permission required' : pendingSync ? 'Sync required' : configured ? 'Webhook configured' : 'Setup required'}
                    </span>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                    {configured ? 'Shopify is configured to send inventory updates to the webhook below.' : 'Connect Shopify inventory updates to Back in Stock.'}
                </p>
                {!checking && permissionRequired && <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                    {permissionsLink && <>
                        <p className="mb-3">Allow inventory access to use Back in Stock. Approve it in Shopify, then return here to check your access.</p>
                        {permissionsLink}
                    </>}
                    {scopeAccess.missingScopes.includes('read_products') && <p className="mt-2">Product access is also required. Contact support to restore the app’s product permission.</p>}
                    <button type="button" onClick={fetchSubscriptions} disabled={busy}
                        className="mt-3 block font-medium underline focus:outline-none focus:ring-2 focus:ring-blue-500">Check permissions again</button>
                </div>}
                {checkError && <div role="alert" className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                    <p className="font-medium">We couldn’t verify your connection.</p>
                    <p className="mt-1 break-words">{checkError}</p>
                    <button type="button" onClick={fetchSubscriptions} disabled={busy}
                        className="mt-2 font-medium underline focus:outline-none focus:ring-2 focus:ring-blue-500">Retry status check</button>
                </div>}
                {pendingCleanup && <div role="alert" className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                    <p>The webhook was deleted from Shopify. Finish syncing the deletion to clear its saved configuration.</p>
                    <button type="button" disabled={busy} onClick={() => deleteWebhook(pendingCleanup, true)}
                        className="mt-2 font-medium underline focus:outline-none focus:ring-2 focus:ring-blue-500">{deletingId ? 'Syncing…' : 'Retry cleanup'}</button>
                </div>}
                {pendingSync && !pendingCleanup && <div role="alert" className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                    <p>The Shopify webhook exists, but configuration is incomplete. Sync it with Back in Stock to finish setup.</p>
                    <button type="button" disabled={busy || !canConfigure} onClick={() => configureWebhook(pendingSync)}
                        className="mt-2 font-medium underline focus:outline-none focus:ring-2 focus:ring-blue-500">{loading ? 'Syncing…' : 'Retry sync'}</button>
                </div>}
                {!checking && !configured && !pendingCleanup && !pendingSync && <ol className="mt-4 divide-y divide-gray-100">
                    <li className="flex items-start gap-3 pb-4">
                        <span aria-hidden="true" className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">1</span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">Allow Shopify access</p>
                            <p className="mt-1 text-xs leading-relaxed text-gray-500">{scopeAccess?.reauthRequired === false ? 'Shopify permissions accepted.' : 'Approve the required access before connecting inventory updates.'}</p>
                        </div>
                    </li>
                    <li className="flex items-start gap-3 pt-4">
                        <span aria-hidden="true" className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">2</span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">Connect inventory updates</p>
                            <p className="mb-3 mt-1 text-xs leading-relaxed text-gray-500">After approving access, configure the webhook for this store.</p>
                            <button type="button" onClick={() => configureWebhook()} disabled={busy || !canConfigure}
                                className={`${buttonClass} border-transparent bg-green-600 text-white hover:bg-green-700`}>
                                {loading ? 'Configuring…' : 'Configure webhook'}
                            </button>
                        </div>
                    </li>
                </ol>}
                {!checking && configured && <details className="mt-4 border-t border-gray-100 pt-3">
                    <summary className="cursor-pointer rounded text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">Connection details</summary>
                    <div className="mt-3 space-y-3">
                        {subscriptions.map((sub) => <dl key={sub.id} className="rounded-md bg-gray-50 p-3 text-xs">
                            <dt className="font-medium text-gray-500">Webhook URL</dt>
                            <dd className="mt-1 break-all font-mono text-gray-700">{sub.callbackUrl || 'Not provided'}</dd>
                            <dt className="mt-3 font-medium text-gray-500">Subscription ID</dt>
                            <dd className="mt-1 break-all font-mono text-gray-700">{sub.id}</dd>
                            <dd className="mt-3">
                                <button type="button" onClick={() => configureWebhook(sub.id)} disabled={busy || !canConfigure || Boolean(pendingCleanup)}
                                    className={`${buttonClass} mr-2 mb-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>Sync with Back in Stock</button>
                                <button type="button" onClick={() => deleteWebhook(sub.id)} disabled={busy || Boolean(pendingCleanup)}
                                    className={`${buttonClass} border-red-200 bg-white text-red-700 hover:bg-red-50`}>
                                    {deletingId === sub.id ? 'Deleting…' : 'Delete webhook'}
                                </button>
                            </dd>
                        </dl>)}
                        <div className="flex flex-wrap items-center gap-2">
                            <button type="button" onClick={fetchSubscriptions} disabled={busy}
                                className={`${buttonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>Refresh status</button>
                        </div>
                    </div>
                </details>}
            </div>
        </section>
    );
};

export default BackInStockHeader;
